import { useCallback, useEffect, useRef, useState } from 'react';
import { hasStatisticsConsent } from '../services/providers/GoogleAnalyticsProvider';
import { GA_CONNECTOR_READY_EVENT } from '../services/providers/GAConnectorProvider';

export const GA_CONNECTOR_TRACKING_ID = '9d0dcebf01901722946f5eb3a3fee19a';
export const GA_CONNECTOR_INTERNAL_DOMAINS = [
  'audicus.com',
  'audicushearingaids.typeform.com',
  'typeform.com',
  'unbounce.com',
  'pages.ubembed.com',
  'ubembed.com',
  '41cbdfe6d86e4ad4b837414dde1b562a.pages.ubembed.com',
];

const CONSENT_EVENTS = [
  'CookiebotOnAccept',
  'CookiebotOnConsentReady',
  'CookiebotOnDecline',
] as const;

const SCRIPT_WAIT_MS = 100;
const SCRIPT_WAIT_TIMEOUT_MS = 8000;

export const fireGAConnectorTrack = (): boolean => {
  if (hasStatisticsConsent() !== true) {
    return false;
  }
  if (typeof window.gaconnector2?.track !== 'function') {
    return false;
  }
  window.gaconnector2.track(GA_CONNECTOR_TRACKING_ID, {
    internalDomains: GA_CONNECTOR_INTERNAL_DOMAINS,
  });
  return true;
};

export default function useTrackGAConnector() {
  const [isGAConnectorEventTracked, setIsGAConnectorEventTracked] =
    useState(false);
  const settledRef = useRef(false);

  const trackGAConnectorEvent = useCallback(() => {
    if (settledRef.current) {
      return;
    }

    if (hasStatisticsConsent() === false) {
      settledRef.current = true;
      setIsGAConnectorEventTracked(true);
      return;
    }

    if (fireGAConnectorTrack()) {
      settledRef.current = true;
      setIsGAConnectorEventTracked(true);
    }
  }, []);

  useEffect(() => {
    trackGAConnectorEvent();
    if (settledRef.current) {
      return undefined;
    }

    let intervalId: number | undefined;
    let timeoutId: number | undefined;
    const stopWaiting = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const waitForScriptAfterConsent = () => {
      if (settledRef.current || hasStatisticsConsent() !== true) {
        return;
      }
      if (intervalId !== undefined) {
        return;
      }
      intervalId = window.setInterval(() => {
        trackGAConnectorEvent();
        if (settledRef.current) {
          stopWaiting();
        }
      }, SCRIPT_WAIT_MS);
      timeoutId = window.setTimeout(stopWaiting, SCRIPT_WAIT_TIMEOUT_MS);
    };

    const onConsentOrReady = () => {
      trackGAConnectorEvent();
      waitForScriptAfterConsent();
    };

    CONSENT_EVENTS.forEach((event) =>
      window.addEventListener(event, onConsentOrReady),
    );
    window.addEventListener(GA_CONNECTOR_READY_EVENT, onConsentOrReady);
    waitForScriptAfterConsent();

    return () => {
      CONSENT_EVENTS.forEach((event) =>
        window.removeEventListener(event, onConsentOrReady),
      );
      window.removeEventListener(GA_CONNECTOR_READY_EVENT, onConsentOrReady);
      stopWaiting();
    };
  }, [trackGAConnectorEvent]);

  return {
    isGAConnectorEventTracked,
    trackGAConnectorEvent,
  };
}
